import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { anomalyApi } from '../../services/api';
import useAuthStore from '../../store/authStore';
import type { PlantHealthSummary } from '../../types';

interface GaugeProps {
  score: number;
  label: string;
  size?: number;
}

function HealthGauge({ score, label, size = 72 }: GaugeProps) {
  const r = size * 0.38;
  const cx = size / 2;
  const cy = size / 2;
  const circumference = 2 * Math.PI * r;
  const filled = (score / 100) * circumference;
  const gap = circumference - filled;

  const color =
    score >= 85 ? '#16a34a' :
    score >= 60 ? '#d97706' :
    '#dc2626';

  const textColor =
    score >= 85 ? '#15803d' :
    score >= 60 ? '#b45309' :
    '#b91c1c';

  return (
    <div className="flex flex-col items-center gap-1">
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        {/* Track */}
        <circle cx={cx} cy={cy} r={r} fill="none" stroke="#e2e8f0" strokeWidth={size * 0.12} />
        {/* Progress */}
        <circle
          cx={cx} cy={cy} r={r}
          fill="none"
          stroke={color}
          strokeWidth={size * 0.12}
          strokeDasharray={`${filled} ${gap}`}
          strokeDashoffset={circumference * 0.25}
          strokeLinecap="round"
          style={{ transition: 'stroke-dasharray 0.6s ease' }}
        />
        {/* Score text */}
        <text x={cx} y={cy + 1} textAnchor="middle" dominantBaseline="middle"
          fontSize={size * 0.18} fontWeight="700" fill={textColor} fontFamily="monospace">
          {score}
        </text>
        <text x={cx} y={cy + size * 0.19} textAnchor="middle"
          fontSize={size * 0.11} fill="#94a3b8">
          %
        </text>
      </svg>
      <p className="text-xs text-slate-500 text-center leading-tight">{label}</p>
    </div>
  );
}

export default function HealthScorePanel() {
  const { user } = useAuthStore();
  const plantId = user?.plant_id ?? 1;

  const { data, isLoading } = useQuery<PlantHealthSummary>({
    queryKey: ['plant-health', plantId],
    queryFn: () => anomalyApi.health(plantId).then((r) => r.data.data),
    refetchInterval: 60_000,
    staleTime: 55_000,
  });

  if (isLoading || !data) {
    return (
      <div className="card">
        <h3 className="text-sm font-semibold text-slate-700 mb-3">Health Scores</h3>
        <div className="flex justify-center items-center h-20 text-slate-400 text-sm">
          Computing baselines…
        </div>
      </div>
    );
  }

  const getScore = (tagName: string): number =>
    data.tag_scores.find((t) => t.tag_name === tagName)?.health_score ?? 100;

  const genScore    = getScore('Generator.Power');
  const turbScore   = getScore('Turbine.Speed');
  const tempScore   = getScore('Generator.Temperature');
  const waterScore  = getScore('Plant.WaterLevel');

  const overallColor =
    data.overall_health >= 85 ? 'text-emerald-700' :
    data.overall_health >= 60 ? 'text-amber-600' : 'text-red-600';

  const criticalWarnings = data.early_warnings.filter((w) => w.severity === 'critical').length;
  const warningCount     = data.early_warnings.filter((w) => w.severity === 'warning').length;

  return (
    <div className="card">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-semibold text-slate-700">Equipment Health</h3>
        <span className={`text-xs font-bold ${overallColor}`}>
          Overall: {data.overall_health}%
        </span>
      </div>

      <div className="flex justify-around items-start mb-3">
        <HealthGauge score={data.overall_health} label="Plant" size={72} />
        <HealthGauge score={genScore}    label="Generator"  size={60} />
        <HealthGauge score={turbScore}   label="Turbine"    size={60} />
        <HealthGauge score={tempScore}   label="Thermal"    size={60} />
        <HealthGauge score={waterScore}  label="Hydrology"  size={60} />
      </div>

      {/* Summary badges */}
      <div className="flex gap-2 flex-wrap">
        <span className="text-xs px-2 py-1 rounded-full bg-emerald-100 text-emerald-700 border border-emerald-300">
          {data.healthy_tags} Healthy
        </span>
        {data.warning_tags > 0 && (
          <span className="text-xs px-2 py-1 rounded-full bg-amber-100 text-amber-700 border border-amber-300">
            {data.warning_tags} Warning
          </span>
        )}
        {data.critical_tags > 0 && (
          <span className="text-xs px-2 py-1 rounded-full bg-red-100 text-red-700 border border-red-300">
            {data.critical_tags} Critical
          </span>
        )}
        {criticalWarnings > 0 && (
          <span className="text-xs px-2 py-1 rounded-full bg-red-100 text-red-700 border border-red-400 font-semibold animate-pulse">
            {criticalWarnings} Early Warning
          </span>
        )}
        {warningCount > 0 && criticalWarnings === 0 && (
          <span className="text-xs px-2 py-1 rounded-full bg-amber-100 text-amber-600 border border-amber-300">
            {warningCount} Advisory
          </span>
        )}
      </div>

      {/* Top warnings preview */}
      {data.early_warnings.length > 0 && (
        <div className="mt-3 space-y-1">
          {data.early_warnings.slice(0, 2).map((w) => (
            <div
              key={w.id}
              className={`text-xs p-2 rounded border ${
                w.severity === 'critical'
                  ? 'border-red-400/60 bg-red-50 text-red-700'
                  : 'border-amber-400/60 bg-amber-50 text-amber-700'
              }`}
            >
              {w.message}
            </div>
          ))}
          {data.early_warnings.length > 2 && (
            <p className="text-xs text-slate-400 text-right">
              +{data.early_warnings.length - 2} more in Anomaly Engine →
            </p>
          )}
        </div>
      )}
    </div>
  );
}
